import { DateTime } from "luxon";

/**
 * Synthesize a VTIMEZONE block for an IANA zone, accurate around `refYear`.
 *
 * We probe the zone's UTC offset across the reference year and emit STANDARD
 * (and DAYLIGHT, when the zone observes DST that year) sub-components with the
 * real transition instants. This is a "near-the-event" approximation — it does
 * not emit perpetual RRULE transition rules — which every importer tolerates,
 * and which our own importer ignores entirely (it reads the TZID name only).
 */
export function buildVTimezone(tz: string, refYear: number): string {
  const janOffset = offsetMinutes(tz, Date.UTC(refYear, 0, 15));
  const julOffset = offsetMinutes(tz, Date.UTC(refYear, 6, 15));

  const lines = [`BEGIN:VTIMEZONE`, `TZID:${tz}`];

  if (janOffset === julOffset) {
    lines.push(...subComponent("STANDARD", refYear, 0, janOffset, janOffset));
  } else {
    // Northern hemisphere: standard = Jan offset (smaller), daylight = Jul.
    const toDst = findTransition(tz, Date.UTC(refYear, 0, 15), Date.UTC(refYear, 6, 15));
    const toStd = findTransition(tz, Date.UTC(refYear, 6, 15), Date.UTC(refYear, 11, 15));
    lines.push(...subComponent("DAYLIGHT", refYear, toDst, janOffset, julOffset));
    lines.push(...subComponent("STANDARD", refYear, toStd, julOffset, janOffset));
  }

  lines.push(`END:VTIMEZONE`);
  return lines.join("\r\n");
}

function offsetMinutes(tz: string, ms: number): number {
  return DateTime.fromMillis(ms, { zone: tz }).offset;
}

/** Binary-search the offset-change instant (ms) in [lo, hi] to minute precision. */
function findTransition(tz: string, lo: number, hi: number): number {
  const startOff = offsetMinutes(tz, lo);
  while (hi - lo > 60_000) {
    const mid = Math.floor((lo + hi) / 2);
    if (offsetMinutes(tz, mid) === startOff) lo = mid;
    else hi = mid;
  }
  return hi;
}

function subComponent(kind: "STANDARD" | "DAYLIGHT", refYear: number, transitionMs: number, fromOff: number, toOff: number): string[] {
  // DTSTART in a VTIMEZONE is local wall time expressed in the *from* offset.
  const wallMs = (transitionMs || Date.UTC(refYear, 0, 1)) + fromOff * 60_000;
  const d = new Date(wallMs);
  const dt =
    `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
    `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}`;
  return [
    `BEGIN:${kind}`,
    `DTSTART:${dt}`,
    `TZOFFSETFROM:${formatOffset(fromOff)}`,
    `TZOFFSETTO:${formatOffset(toOff)}`,
    `END:${kind}`,
  ];
}

function formatOffset(min: number): string {
  const sign = min < 0 ? "-" : "+";
  const abs = Math.abs(min);
  return `${sign}${p(Math.floor(abs / 60))}${p(abs % 60)}`;
}

function p(n: number): string {
  return String(n).padStart(2, "0");
}
